import { useContext, useEffect, useState } from 'react'

import { AuthContext } from '../../../context/AuthContext'
import { getSessionById } from '../../../service/sessionService'
import { enterExperiment } from '../../../service/experimentService'
import { studentPathFor } from '../studentView/studentSurface'
import { conditionTagOf, isExperimentSession } from './experimentConfig'

// Redirects already settled in this page load, by `${userId}:${routeSessionId}`. Kept outside
// the component for the same reason as useSessionJoin's failedJoins: setCurrentUser remounts
// the page (App.js defines ProtectedRoute inside App), and the remounted page must not ask
// again. Placement is sticky, so a redirect stays right; 'open' is not kept - a later visit
// may need the join again (they worked elsewhere in between), and asking is idempotent.
const settled = new Map()

// A class opening the link together must not retry in lockstep: each wait is jittered, and
// after the quick retries the page keeps trying every ~15s while showing Try again. Home.jsx
// retries its route fetch the same way.
export const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000]
export const SLOW_RETRY_MS = 15000
export const delayFor = (attempt) =>
  (RETRY_DELAYS_MS[attempt - 1] ?? SLOW_RETRY_MS) * (0.75 + Math.random() * 0.5)
export const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const OPEN = { status: 'open' }
const CHECKING = { status: 'checking' }

// A student's link to a session that does not exist - a mistyped number (E1-S3: 995 for 955)
// showed "Opening your exercise…" for good. The number is in the message, so the student and
// the instructor see which one was typed. Pages keep checking now and then, as an empty answer
// can also be a busy server.
export const missingSession = (sessionId) => ({
  status: 'blocked',
  missing: true,
  title: "There's no exercise at this link",
  message: `This link ends in ${sessionId}. Check that number against the link your instructor gave you.`,
})

const isStudentRole = (user) => user?.role === 3 || user?.role === 4

// Whether a student opening `row` has to ask the experiment where they belong: on the
// experiment's own link always; on a condition's link unless they already work in it.
export const needsExperimentEntry = (row, user) =>
  isStudentRole(user) &&
  Boolean(row) &&
  (isExperimentSession(row) ||
    (Boolean(conditionTagOf(row)) && Number(user.session_id) !== Number(row.id)))

// For a student opening /session/:id or /workspace/:id, whether this page is theirs to work
// on (POST /experiments/:id/enter decides, and joins them to their condition):
//   - the experiment's own link sends them to the condition they are placed in;
//   - a condition's link opens if it is theirs, or sends them to theirs ('balanced' mode);
//   - any other session opens as before, without asking anything.
// `routeRow` is the route's session row when the page has it. With `fetchRow` the hook
// fetches the row itself while the page doesn't have it; without, it waits for the page.
// Staff are never asked.
// Returns { status, to?, title?, message?, attempt?, retry? }, status one of
//   'open'      the page carries on (its own join finds the student already joined);
//   'checking'  asking the server - it retries on its own;
//   'slow'      still retrying after the quick retries; `retry` tries again now;
//   'redirect'  their exercise is at `to`;
//   'blocked'   nothing to open here: `title` and `message` say why; `signIn` when signing in
//               again (another account, or a real one instead of a guest) can open it;
//               `missing` (with `retry`) when no session has this id - checked again now and
//               then, and the page carries on by itself once it is there.
export const useExperimentEntry = (routeSessionId, routeRow, { fetchRow = false } = {}) => {
  const { currentUser, setCurrentUser } = useContext(AuthContext)
  const key = `${currentUser?.id}:${routeSessionId}`
  const row = routeRow && String(routeRow.id) === String(routeSessionId) ? routeRow : null
  // Held with the key it answers: React Router reuses the page across /session/:a ->
  // /session/:b, and b must not act on a's answer.
  const [held, setHeld] = useState(() => ({ key, answer: settled.get(key) || null }))
  const [round, setRound] = useState(0)
  const answer = held.key === key ? held.answer : settled.get(key) || null

  const student = isStudentRole(currentUser)
  // Nothing to ask: staff, an ordinary session, or the student's own condition.
  const local = !student || (row && !needsExperimentEntry(row, currentUser)) ? OPEN : null
  const waitingForPage = !local && !row && !fetchRow

  useEffect(() => {
    if (local || waitingForPage) return
    let cancelled = false
    // Whether the page says the session is missing: the checks then go on quietly, and the
    // first row found clears it - an /enter that fails after that is 'slow', not 'missing'.
    let missing = false
    const setAnswer = (value) => setHeld({ key, answer: value })
    const done = settled.get(key)
    if (done) {
      setAnswer(done)
      return
    }
    setAnswer(CHECKING)

    const settle = (result, user) => {
      if (result.status === 'redirect') settled.set(key, result)
      setAnswer(result)
      // The server joined them: carry that into the frontend's user, so the page's own join
      // (useSessionJoin, Home.jsx) sees session_id already pointing here and only confirms it.
      if (user) {
        setCurrentUser({
          ...currentUser,
          session_id: user.session_id,
          session_list: user.session_list,
          group_id: user.group_id ?? null,
          code_id: user.code_id ?? null,
        })
      }
    }

    // One try: a result, or null to try again.
    const attemptOnce = async (attempt) => {
      let target = row
      if (!target) {
        try {
          target = await getSessionById(routeSessionId)
        } catch (error) {
          return error?.response?.status === 404 ? { result: missingSession(routeSessionId) } : null
        }
        // An empty answer is also what the server sends when its database is too busy: try
        // again a few times before taking it as a missing session.
        if (!target) return attempt < RETRY_DELAYS_MS.length ? null : { result: missingSession(routeSessionId) }
        if (missing && !cancelled) {
          missing = false
          setAnswer(CHECKING)
        }
        if (!needsExperimentEntry(target, currentUser)) return { result: OPEN }
      }
      const experimentRoute = isExperimentSession(target)
      const response = await enterExperiment(
        experimentRoute ? target.id : conditionTagOf(target).experimentId,
        experimentRoute ? undefined : target.id,
        // Only a Cognito sign-in (users with a uid) is refreshed: for a guest, a Cognito
        // session left in this browser by someone else must not replace their token.
        { refresh: Boolean(currentUser.uid) }
      )
      if (response.ok) {
        const here = Number(response.sessionId) === Number(routeSessionId)
        return {
          result: here
            ? OPEN
            : { status: 'redirect', to: studentPathFor({ id: response.sessionId, type: response.sessionType }) },
          user: response.user,
        }
      }
      if (!response.status || response.status >= 500 || response.status === 429) return null
      // The server sees an instructor or TA where this page has a student: the request went
      // out under someone else's sign-in.
      if (response.status === 401 || response.staff) {
        return {
          result: {
            status: 'blocked',
            title: 'Please sign in again',
            message: 'Your sign-in has expired. Sign in, then open the link again.',
            signIn: true,
          },
        }
      }
      if (response.status === 404) {
        return {
          result: {
            status: 'blocked',
            title: 'This exercise is no longer available',
            message: 'Ask your instructor for the exercise link.',
          },
        }
      }
      return {
        result: {
          status: 'blocked',
          title: "This link isn't open",
          message: response.message || 'Ask your instructor for the exercise link.',
          // 403: the study asks for a VizPI account, and this is a guest.
          ...(response.status === 403 ? { signIn: true } : {}),
        },
      }
    }

    const run = async () => {
      for (let attempt = 0; ; attempt++) {
        if (attempt > 0) {
          if (attempt > RETRY_DELAYS_MS.length && !missing) setAnswer({ status: 'slow', attempt })
          await wait(delayFor(attempt))
          if (cancelled) return
        }
        const outcome = await attemptOnce(attempt)
        if (cancelled) return
        if (outcome?.result?.missing) {
          missing = true
          setAnswer(outcome.result)
        } else if (outcome) {
          settle(outcome.result, outcome.user)
          return
        }
      }
    }
    run()
    return () => {
      cancelled = true
    }
    // currentUser is read, not watched: the join this sets would restart the check. `row` is
    // watched by id, so the page's poll replacing the object does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, Boolean(local), waitingForPage, row?.id, round])

  if (local) return OPEN
  if (!answer) return CHECKING
  if (answer.status === 'slow' || answer.missing) return { ...answer, retry: () => setRound((value) => value + 1) }
  return answer
}
